import type { PlaygroundTestProgress, PlaygroundTestRunResult, PlaygroundVirtualRun } from '@gotti/playground/virtual';
import type { BootedGame, SandboxTest, SandboxTestSide } from './sandboxTest';

/**
 * How a `defineSandboxTest` test runs on the playground's runner (Phase 5 of the sandbox-on-playground spec): its booter
 * mounts it with the game — lowered here into the playground test the mounted definition carries — and runs it through
 * that mount (`runTest`), where the DSL's executor (`virtual.run`) drives the booter's virtual clock. Kept out of
 * sandboxTest.ts on purpose: that file's declarations are the AI's ambient `@gotti/sandbox-test` module, and none of this
 * is the AI's to write or call.
 */

/**
 * One side of a lowered step, as the playground's progress tree shows it: the side's action (a press, click, type, act or
 * disconnect) as its `onUpdate`, its `assert` as the check it runs after the step settles. Never run by the playground:
 * the executor runs the step; these only name what the tree shows.
 */
export interface LoweredSandboxSide {
  onUpdate?: () => void;
  afterUpdate: { label: string; run: () => Promise<void> }[];
}

/** A DSL test lowered for a playground definition's `tests`: its steps as the progress tree shows them, and its run. */
export interface LoweredSandboxTest {
  label: string;
  steps: { label: string; atTime: number; client?: LoweredSandboxSide; server?: LoweredSandboxSide }[];
  virtual: PlaygroundVirtualRun;
}

/** What `runSandboxTest` hands a booter beside the test's latency: the test, lowered, for the definition it mounts. */
export interface PlaygroundBootOptions {
  latencyMs?: number;
  test?: LoweredSandboxTest;
}

/** A booted game that runs its test on its playground mount (both real booters): the mount's run, then its teardown. */
export interface PlaygroundBootedGame extends BootedGame {
  runTest(onProgress?: (progress: PlaygroundTestProgress) => void): Promise<PlaygroundTestRunResult>;
}

/** Whether the booter mounted the test with the game, to run it there. */
export const runsOnPlayground = (game: BootedGame): game is PlaygroundBootedGame =>
  typeof (game as Partial<PlaygroundBootedGame>).runTest === 'function';

/** Shown, never run: the executor runs the step. */
const shownOnly = (): void => undefined;
const shownCheck = async (): Promise<void> => undefined;

/** `side` as the tree shows it — its action, then its assert — or nothing for a side the step does not declare. */
const lowerSide = (side: SandboxTestSide<any> | undefined): LoweredSandboxSide | undefined => {
  if (!side) return undefined;
  // What the executor counts as an interaction (it settles a round trip for one): any of these.
  const acts = side.press !== undefined || !!side.click || side.type !== undefined || !!side.act || !!side.disconnect;
  return {
    ...(acts ? { onUpdate: shownOnly } : {}),
    afterUpdate: side.assert ? [{ label: 'assert', run: shownCheck }] : [],
  };
};

/**
 * `test` as the playground test its booter's definition carries: the steps in the order the executor runs them — by
 * `atTime`, ties in declared order, so the executor's event `step` indexes this list — each with its sides as the
 * progress tree shows them, and `virtual`, which runs it. A malformed test (no `steps` array) lowers to no steps:
 * `runSandboxTest` refuses it after the boot, as it always has.
 */
export function sandboxTestToPlayground(
  test: Pick<SandboxTest<any, any>, 'label' | 'steps'>,
  virtual: PlaygroundVirtualRun,
): LoweredSandboxTest {
  const steps = Array.isArray(test.steps) ? [...test.steps].sort((a, b) => a.atTime - b.atTime) : [];
  return {
    label: test.label,
    steps: steps.map((step) => {
      const client = lowerSide(step.client);
      const server = lowerSide(step.server);
      return { label: step.label, atTime: step.atTime, ...(client ? { client } : {}), ...(server ? { server } : {}) };
    }),
    virtual,
  };
}
